import { useMemo, useState } from 'react';
import { FEATURES, type FeatureGrant, type FeatureLevel } from '@visionqc/shared';
import { api } from '../api/client';

/**
 * New-customer onboarding, as one flow rather than four disconnected screens.
 *
 * Creating a customer was only ever step one: in practice a new customer also
 * needs billing details so they can be invoiced, a way in (a portal login), and
 * a set of functions turned on — and those each lived on a different page reached
 * only after the fact. This wizard does them together — company → invoicing →
 * login → access — so the person who signs the customer up leaves with a tenant
 * that is billable and usable, not an empty shell.
 *
 * Every step after the name is optional: an internally-operated customer may
 * have no login, billing can be completed later, and access tuned any time on the
 * customer's own page. Only the name is required to create the record.
 */

type Level = FeatureLevel | 'off';
type Step = 1 | 2 | 3 | 4;
const STEPS: Array<{ n: Step; label: string }> = [
  { n: 1, label: 'Company' },
  { n: 2, label: 'Invoicing' },
  { n: 3, label: 'Login' },
  { n: 4, label: 'Access' },
];

// A sensible opening hand: the two things nearly every customer watches are on
// in view mode, everything else off. The operator tunes from there.
const DEFAULT_LEVELS: Record<string, Level> = Object.fromEntries(
  FEATURES.map((f) => [f.key, f.key === 'live' || f.key === 'dashboard' ? 'view' : 'off']),
);

const FIELD: React.CSSProperties = { width: '100%' };
const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 2 };

export function CustomerOnboardWizard({
  onClose,
  onDone,
}: {
  onClose: () => void;
  /** Hands back the new customer id so the caller can open its detail page. */
  onDone: (customerId: number) => void;
}) {
  const [step, setStep] = useState<Step>(1);

  // Company
  const [name, setName] = useState('');

  // Invoicing / billing
  const [billable, setBillable] = useState(true);
  const [legalName, setLegalName] = useState('');
  const [legalNameTouched, setLegalNameTouched] = useState(false);
  const [vatNumber, setVatNumber] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Netherlands');
  const [currency, setCurrency] = useState('EUR');
  const [paymentTermsDays, setPaymentTermsDays] = useState('30');

  // Login
  const [makeLogin, setMakeLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [loginName, setLoginName] = useState('');
  const [loginNameTouched, setLoginNameTouched] = useState(false);
  const [password, setPassword] = useState('');

  // Access
  const [levels, setLevels] = useState<Record<string, Level>>(DEFAULT_LEVELS);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Legal name defaults to the trading name; the billing email to the login
  // email. Both give way the moment the operator types their own.
  const effectiveLegalName = legalNameTouched ? legalName : legalName || name;
  const effectiveLoginName = loginNameTouched ? loginName : loginName || email;

  const grantedCount = useMemo(
    () => FEATURES.filter((f) => levels[f.key] && levels[f.key] !== 'off').length,
    [levels],
  );

  const canLeaveLogin =
    !makeLogin ||
    (email.trim().length > 0 && password.length > 0 && effectiveLoginName.trim().length > 0);

  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      // 1. The customer — everything else hangs off its id.
      const customer = await api.createCustomer({ name: name.trim() });

      // 2. Billing details, so the customer is invoice-ready from day one. Only
      //    the fields that were filled in are sent; blanks are left untouched.
      const billing: Record<string, unknown> = { billable };
      const put = (k: string, v: string) => {
        if (v.trim()) billing[k] = v.trim();
      };
      put('legalName', effectiveLegalName);
      put('vatNumber', vatNumber);
      put('billingEmail', billingEmail);
      put('street', street);
      put('houseNumber', houseNumber);
      put('postalCode', postalCode);
      put('city', city);
      put('country', country);
      if (currency.trim().length === 3) billing.currency = currency.trim().toUpperCase();
      const terms = Number(paymentTermsDays);
      if (Number.isFinite(terms) && terms >= 0) billing.paymentTermsDays = terms;
      await api.updateCustomer(customer.id, billing);

      // 3. Access. A failure here must not hide that the customer now exists, so
      //    we still open it and let its page show what landed.
      const grants: FeatureGrant[] = FEATURES.filter((f) => levels[f.key] !== 'off').map((f) => ({
        key: f.key,
        level: levels[f.key] as FeatureLevel,
      }));
      if (grants.length > 0) {
        await api.setCustomerFeatures(customer.id, grants);
      }

      // 4. The login last — role CUSTOMER needs the customerId we now have.
      if (makeLogin) {
        await api.createUser({
          name: effectiveLoginName.trim(),
          role: 'CUSTOMER',
          active: true,
          customerId: customer.id,
          email: email.trim() || null,
          password,
        });
      }

      onDone(customer.id);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const LEVEL_OPTS: Array<{ value: Level; label: string }> = [
    { value: 'off', label: 'Off' },
    { value: 'view', label: 'View' },
    { value: 'operate', label: 'Operate' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
        zIndex: 50,
      }}
      onClick={busy ? undefined : onClose}
    >
      <div
        className="panel"
        style={{ maxWidth: 580, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="New customer"
      >
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0 }}>New customer</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Step {step} of {STEPS.length}
          </span>
        </div>

        {/* Step rail — named so the operator knows what is still to come. */}
        <div className="row" style={{ gap: 6, margin: '12px 0 18px' }}>
          {STEPS.map(({ n, label }) => {
            const done = n < step;
            const active = n === step;
            return (
              <div
                key={label}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '6px 4px',
                  borderRadius: 6,
                  background: active ? 'var(--accent)' : done ? '#2e7d32' : 'transparent',
                  color: active || done ? '#fff' : 'var(--muted)',
                  border: '1px solid var(--border)',
                }}
              >
                {done ? '✓ ' : ''}
                {label}
              </div>
            );
          })}
        </div>

        {error && <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {step === 1 && (
          <div className="grid" style={{ gap: 10 }}>
            <div>
              <label style={LABEL}>Customer name</label>
              <input
                autoFocus
                placeholder="e.g. ACME Cookies"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && name.trim() && setStep(2)}
                style={FIELD}
              />
            </div>
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>
              The trading name you know this company by. Their legal name for invoices comes next.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="grid" style={{ gap: 12 }}>
            <label className="row" style={{ gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={billable}
                onChange={(e) => setBillable(e.target.checked)}
              />
              <span style={{ fontSize: 14, fontWeight: 600 }}>This customer is billable</span>
            </label>
            <p className="muted" style={{ fontSize: 12, margin: '-4px 0 0' }}>
              Usage is metered and pushed to invoicing each month. Turn off for a pilot or an
              internal account.
            </p>

            <div>
              <label style={LABEL}>Legal name (on the invoice)</label>
              <input
                placeholder="Defaults to the customer name"
                value={effectiveLegalName}
                onChange={(e) => {
                  setLegalNameTouched(true);
                  setLegalName(e.target.value);
                }}
                style={FIELD}
              />
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>VAT number</label>
                <input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} style={FIELD} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>Billing email</label>
                <input
                  type="email"
                  placeholder="invoices@company.com"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  style={FIELD}
                />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div style={{ flex: 3 }}>
                <label style={LABEL}>Street</label>
                <input value={street} onChange={(e) => setStreet(e.target.value)} style={FIELD} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>No.</label>
                <input
                  value={houseNumber}
                  onChange={(e) => setHouseNumber(e.target.value)}
                  style={FIELD}
                />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>Postal code</label>
                <input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  style={FIELD}
                />
              </div>
              <div style={{ flex: 2 }}>
                <label style={LABEL}>City</label>
                <input value={city} onChange={(e) => setCity(e.target.value)} style={FIELD} />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={LABEL}>Country</label>
                <input value={country} onChange={(e) => setCountry(e.target.value)} style={FIELD} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>Currency</label>
                <input
                  maxLength={3}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  style={FIELD}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={LABEL}>Terms (days)</label>
                <input
                  type="number"
                  min={0}
                  value={paymentTermsDays}
                  onChange={(e) => setPaymentTermsDays(e.target.value)}
                  style={FIELD}
                />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="grid" style={{ gap: 12 }}>
            <label className="row" style={{ gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={makeLogin}
                onChange={(e) => setMakeLogin(e.target.checked)}
              />
              <span style={{ fontSize: 14, fontWeight: 600 }}>Create a first portal login</span>
            </label>
            <p className="muted" style={{ fontSize: 12, margin: '-4px 0 0' }}>
              A CUSTOMER account that signs in at the portal and sees only this customer. Skip it for
              a customer you operate for.
            </p>

            {makeLogin && (
              <div className="grid" style={{ gap: 10, marginTop: 4 }}>
                <div>
                  <label style={LABEL}>Email</label>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={FIELD}
                  />
                </div>
                <div>
                  <label style={LABEL}>Display name</label>
                  <input
                    placeholder="Defaults to the email"
                    value={effectiveLoginName}
                    onChange={(e) => {
                      setLoginNameTouched(true);
                      setLoginName(e.target.value);
                    }}
                    style={FIELD}
                  />
                </div>
                <div>
                  <label style={LABEL}>Password</label>
                  <input
                    type="password"
                    placeholder="They can change it later"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={FIELD}
                  />
                  <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
                    Give them a password now — an account with no way to sign in is useless.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="grid" style={{ gap: 8 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Functions &amp; access</label>
              <span className="muted" style={{ fontSize: 12 }}>
                {grantedCount} of {FEATURES.length} on
              </span>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 4px' }}>
              What this customer may use when they sign in. Off = hidden; View = may see it; Operate =
              may work in it. You can change this any time on their page.
            </p>
            {FEATURES.map((f) => {
              const level = levels[f.key] ?? 'off';
              return (
                <div
                  key={f.key}
                  className="row"
                  style={{ justifyContent: 'space-between', gap: 12, alignItems: 'center' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{ fontSize: 14, fontWeight: 600, opacity: level === 'off' ? 0.6 : 1 }}
                    >
                      {f.label}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {f.summary}
                    </div>
                  </div>
                  <div
                    className="row"
                    style={{ gap: 0, flexShrink: 0 }}
                    role="group"
                    aria-label={f.label}
                  >
                    {LEVEL_OPTS.map((opt, i) => {
                      const disabled = opt.value === 'operate' && !f.operable;
                      const active = level === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={disabled}
                          title={disabled ? 'This function is view-only' : undefined}
                          onClick={() => setLevels((m) => ({ ...m, [f.key]: opt.value }))}
                          style={{
                            padding: '4px 12px',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            borderRadius:
                              i === 0
                                ? '6px 0 0 6px'
                                : i === LEVEL_OPTS.length - 1
                                  ? '0 6px 6px 0'
                                  : 0,
                            border: '1px solid var(--border)',
                            borderLeft: i === 0 ? '1px solid var(--border)' : 'none',
                            background: active ? 'var(--accent)' : 'transparent',
                            color: active ? '#fff' : disabled ? 'var(--muted)' : 'inherit',
                            opacity: disabled ? 0.4 : 1,
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer controls */}
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 20 }}>
          <button className="btn" onClick={busy ? undefined : onClose} disabled={busy}>
            Cancel
          </button>
          <div className="row" style={{ gap: 8 }}>
            {step > 1 && (
              <button className="btn" onClick={() => setStep((s) => (s - 1) as Step)} disabled={busy}>
                ← Back
              </button>
            )}
            {step < 4 && (
              <button
                className="btn primary"
                disabled={(step === 1 && !name.trim()) || (step === 3 && !canLeaveLogin)}
                onClick={() => setStep((s) => (s + 1) as Step)}
              >
                Next →
              </button>
            )}
            {step === 4 && (
              <button className="btn primary" disabled={busy} onClick={() => void finish()}>
                {busy ? 'Creating…' : 'Create customer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
